/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/pages/auth/AuthPage`, `src/pages/home/HomePage`).
 */

import React, {useEffect} from 'react';
import { Navigate, Route, Routes as RoutesDom } from 'react-router-dom';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { makeStyles, createStyles } from '@material-ui/core';
import { SnackbarProvider } from 'notistack';
import ErrorsPage from '../pages/home/error/ErrorsPage';
import * as auth from '../store/ducks/auth.duck';
import LogoutPage from '../pages/auth/Logout';
import { AuthPage } from '../pages/auth/AuthPage';
import { LayoutContextProvider } from '../../_base';
import Layout from '../../_base/layout/Layout';
import { IAppState } from '../store/rootDuck';
import { withRouter } from './withRouter';
import UsersPage from '../pages/home/users';
import BalanceRequestsPage from '../pages/home/BalanceRequests/BalanceRequestsPage';
import SendReceivePage from '../pages/home/SendReceive/SendReceivePage';
import TransactionPage from '../pages/home/Transactions/TransactionPage'

export type ProtectedRouteProps = {
  isAuthorized: boolean;
  outlet: JSX.Element;
};

export default function ProtectedRoute({ isAuthorized, outlet }: ProtectedRouteProps) {
  if (isAuthorized) {
    return outlet;
  }
  return <Navigate to='/error/error-v1' />;
}

const useStyles = makeStyles(() =>
  createStyles({
    success: {
      backgroundColor: '#0abb87 !important',
    },
    error: {
      backgroundColor: '#DC1F1F !important',
    },
  })
);

export const Routes = withRouter(props => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const { isAuthorized, configMenu, checkMfa, refreshToken } = useSelector(
    ({ auth, builder: { menuConfig } }: IAppState) => ({
      configMenu: menuConfig,
      isAuthorized: auth.user != null,
      checkMfa: auth.checkMfa,
      refreshToken: auth.refreshToken,
    }),
    shallowEqual
  );

  // useEffect(() => {
  //   if(refreshToken)
  //     dispatch(auth.actions.refreshTokenRequest({payload:refreshToken}))
  // }, [refreshToken])

  return (
    <SnackbarProvider
      maxSnack={3}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      style={{ fontSize: 17, textTransform: 'uppercase', fontWeight: 'bold' }}
      classes={{
        variantSuccess: classes.success,
        variantError: classes.error,
      }}
    >
      <LayoutContextProvider history={props} menuConfig={configMenu}>
        <RoutesDom>
          <Route path='auth/*' element={<AuthPage />} />
          <Route path='error/*' element={<ErrorsPage />} />
          <Route path='logout/*' element={<LogoutPage />} />
          <Route path='/' element={<Layout />}>
            <Route path='/' element={<Navigate to='auth/main' replace />} />
            <Route
              path='users/*'
              element={<ProtectedRoute isAuthorized={isAuthorized} outlet={<UsersPage />} />}
            />
            <Route
              path='balancerequests/*'
              element={
                <ProtectedRoute isAuthorized={isAuthorized} outlet={<BalanceRequestsPage />} />
              }
            />

            <Route
              path='sendreceiverequest/*'
              element={
                <ProtectedRoute isAuthorized={isAuthorized} outlet={<SendReceivePage />} />
              }
            />
            
            <Route
              path='transactions/*'
              element={
                <ProtectedRoute isAuthorized={isAuthorized} outlet={<TransactionPage />} />
              }
            />
            <Route path='*' element={<Navigate to='/error/error-v1' />} />
          </Route>
        </RoutesDom>
      </LayoutContextProvider>
    </SnackbarProvider>
  );
});
